import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    getLabellingByLineId,
    getLabellingByWorkorderId,
    getPrintersOfflineByLine,
    onAddPrinted,
    onClearLabellingActivities,
    onClearOfflinePrinters,
    onClearPrinted,
    onSetCombinedPdf,
    onSetCopesPerPalletChanged,
    onSetCopiesCount,
    onSetOffline,
    onSetOfflineTimeStamp,
    onSetPalletCount,
    onSetPrinterId,
    onSetPrintingProgress,
    onSetQtyCount,
    setUserNumberOfCopiesPerPalletRedux
} from "../../redux/slices/labelling.feature";
import _ from "lodash";
import { IRootState } from "../../types/redux/IRootStore";
import { Activity } from "../../types/activity";
import { bootboxAlert } from "../common/bootboxWrapper";
import { useAppDispatch } from "../../redux/hooks";
import { ReactSelectStyle } from "../common/rectSelectStyle";
import { getNumberOfPalletsToPrint, printPallets } from "../../aplApi/printHelper";
import { PrintType } from "../../types/printType";
import { PrintParams } from "../../types/printParams";
import moment from "moment";
import { OfflinePrintersSearchProps } from "../../types/offlinePrintersSearchProps";
import { useTranslation } from "react-i18next";
import { getCombinedPdf } from "../../aplApi/pdfHelper";
import DownloadButton from "../common/Modals/DownloadButton";
import Spinner from "../common/Spinner";
import ReactSelectWrapper, { IReactSelectOption } from "../common/ReactSeletWrapper";
import AplSettingsSelector from "../../redux/helpers/AplSettingsSelector";
import { getCustDescriptionField, getPalletLayers } from "../../aplUtils/activityHelper";
import { getTextColor } from "../../aplUtils/themeHelper";
import { UserPermission } from "../../types/userPermission";
import { clearWorkorderManagement } from "../../redux/slices/workorderItems.feature";
import DateSelector3 from "../common/DateSelector3";
import { getAplTokens } from "../../aplUtils/cookiesWrapper";
import { AplParameter } from "../../types/aplParameter";
import { parseBool } from "../../aplUtils/extensions";
import LabelsPrintBasic from "./LabelsPrintBasic";
import { translateString } from "../../translations/gridTranslationHelper";
import { ReduxStateLabelling } from "../../types/redux/reduxStateLabelling";
import { dateDiffSeconds } from "../../aplUtils/dateHelper";

const LabelsPrint = () => {
    const { t } = useTranslation();
    const params = useParams();
    const aplSettings = AplSettingsSelector();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const lblRdx = useSelector((state: IRootState) => state.labelling);
    const dispAct = lblRdx.activities[0] as Activity;
    const dispQty = lblRdx.selectedQtyCount > 0 ? lblRdx.selectedQtyCount : dispAct?.standardQty ?? 0;
    const dispCopies = lblRdx.selectedCopiesCount > 0 ? lblRdx.selectedCopiesCount : dispAct?.palLabelCopies ?? 0;
    const dispPrnt = lblRdx.offline
        ? lblRdx.selectedPrinterId > 0
            ? lblRdx.selectedPrinterId
            : lblRdx.offlinePrinters[0]?.printerId ?? 0
        : lblRdx.selectedPrinterId > 0
        ? lblRdx.selectedPrinterId
        : dispAct?.printers[0]?.printerId ?? 0;

    const cancelRef = useRef(false);

    var cancelPrinting = () => {
        cancelRef.current = true;
    };

    const handleChangeOfflineMode = React.useCallback(
        (isOffline: boolean, redirect: boolean = true) => {
            dispatch(onSetOffline(isOffline));
            if (isOffline && dispAct != null) {
                dispatch(onSetPalletCount(0));
                if (redirect) navigate(`/labels/${dispAct.lineId}/${dispAct.operationId}`, { replace: true });
            }
            if (!isOffline && dispAct != null) {
                dispatch(onSetPalletCount(1));
                if (redirect) navigate(`/labels/${dispAct.lineId}`, { replace: true });
            }
        },
        [dispAct, dispatch, navigate]
    );

    const refreshDisplayedWorkorder = React.useCallback(() => {
        if (params.hasOwnProperty("operationId")) {
            dispatch(getLabellingByWorkorderId({ operationId: params.operationId! }) as any);
            handleChangeOfflineMode(true);
        } else {
            dispatch(getLabellingByLineId(Number(params.lineId)) as any);
        }
        return () => {
            dispatch(onClearLabellingActivities());
        };
    }, [params, dispatch, handleChangeOfflineMode]);

    const getOfflinePrinters = React.useCallback(() => {
        dispatch(
            getPrintersOfflineByLine({
                lineId: 0,
                departmentId: aplSettings.departmentId,
                siteId: aplSettings.siteId,
                groupByPrinterId: true,
                offline: true
            } as OfflinePrintersSearchProps) as any
        );
    }, [aplSettings.departmentId, aplSettings.siteId, dispatch]);

    const loadData = React.useCallback(
        (lRdx: ReduxStateLabelling, depId: number) => {
            let secDiff = dateDiffSeconds(lRdx.lastUpdated, new Date());
            console.log(`${new Date().toISOString()} - loadData`, secDiff, lRdx.activitiesLoading, lRdx.offlinePrintersLoading);
            if (lRdx.activitiesLoading || lRdx.offlinePrintersLoading || lRdx.labelsLoading || secDiff < 5) return;

            if (getAplTokens().token) {
                if (lRdx.offlinePrinters.length === 0 || lRdx.offlinePrinters[0].departmentId !== depId) {
                    if (lRdx.offlinePrinters.length === 0) {
                        getOfflinePrinters();
                    }
                }
                refreshDisplayedWorkorder();
            }
        },
        [getOfflinePrinters, refreshDisplayedWorkorder]
    );

    React.useEffect(() => {
        const interval = setInterval(() => {
            if (!lblRdx.activitiesLoading) loadData(lblRdx, aplSettings.departmentId);
        }, 15 * 1000);
        loadData(lblRdx, aplSettings.departmentId);
        return () => {
            clearInterval(interval);
        };
    }, [lblRdx, aplSettings.departmentId, loadData, dispatch, getOfflinePrinters]);

    React.useEffect(() => {
        return () => {
            dispatch(onClearOfflinePrinters());
            dispatch(onClearLabellingActivities());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aplSettings.departmentId]);

    // sets path pdf to download in redux
    React.useEffect(() => {
        if (lblRdx.printingProgress === 0 && lblRdx.printed.length > 0) {
            console.log(`${new Date().toISOString()} - printingProgress: ${lblRdx.printingProgress}`, lblRdx.printed);
            getCombinedPdf(lblRdx, dispatch, onSetCombinedPdf);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lblRdx.printed]);

    /// resets selected printer on mode change
    useEffect(() => {
        if (lblRdx.selectedPrinterId > 0) dispatch(onSetPrinterId(0));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lblRdx.offline]);

    const handleDdQuantityChange = (e: any): void => {
        dispatch(onSetQtyCount(e.value));
    };

    const handleChangePrinters = (e: any): void => {
        dispatch(onSetPrinterId(e.value));
    };

    const layersSelectData: IReactSelectOption[] =
        dispAct == null
            ? []
            : _.orderBy(getPalletLayers(dispAct), "layersCount", "desc").map(x => ({
                  value: x.piecesCount,
                  label: `${x.layersCount} (${x.piecesCount})`
              }));

    const printersSelectData: IReactSelectOption[] = lblRdx.offline
        ? lblRdx.offlinePrinters.map((x, y) => ({
              value: x.printerId,
              label: x.printerName
          }))
        : dispAct?.printers == null
        ? []
        : dispAct.printers.map((x, y) => ({
              value: x.printerId,
              label: x.printerName
          }));

    let executePrinting = async () => {
        console.log(`${new Date().toISOString()} - executePrinting`, lblRdx.activities);
        if (lblRdx.activities.length < 1) {
            bootboxAlert(t("main.noActivities"));
            return null;
        }
        let act = lblRdx.activities[0] as Activity;

        if (lblRdx.offline && lblRdx.selectedPalletCount === 0) {
            bootboxAlert(`${t("main.noOfPallets")} = 0`);
            return null;
        }

        let finalPalletCount = getNumberOfPalletsToPrint(act, lblRdx.offline, lblRdx.selectedPalletCount);

        let printParams: PrintParams = {
            siteId: aplSettings.siteId,
            lineId: act.lineId,
            workOrderId: act.workOrderId,
            operationId: act.operationId,
            printerId: dispPrnt,
            quantity: dispQty,
            copies: dispCopies,
            traceabilityBatch: dispAct.palletTraceabilityBatch,
            offline: lblRdx.offline,
            productionDateMoment: lblRdx.offline ? moment(lblRdx.offlineTimeStamp).toObject() : null,
            palletCount: finalPalletCount,
            dontPrint: false,
            workOrderBatchId: null,
            printType: lblRdx.offline ? PrintType.Offline : PrintType.Online
        };

        cancelRef.current = false;
        dispatch(onClearPrinted());
        for (let i: number = 0; i < finalPalletCount; i++) {
            if (cancelRef.current) {
                console.log(`${new Date().toISOString()} loop has terminated`);
                break;
            }
            dispatch(onSetPrintingProgress(i + 1));

            try {
                let results = await printPallets(printParams);
                let resultError = results.data.find(d => !d.success);
                dispatch(onAddPrinted(results.data));
                if (resultError) {
                    bootboxAlert(`${t("main.error")} ${resultError.errorMessage}`);
                    dispatch(onSetPrintingProgress(0));
                    return;
                }
            } catch (exception: any) {
                console.log(`${new Date().toISOString()} printing failed`, exception);
                bootboxAlert(`${t("main.error")} ${exception.message}`);
                dispatch(onSetPrintingProgress(0));
                return;
            }
        }

        dispatch(onSetPrintingProgress(0));
        dispatch(onSetCopiesCount(0));
        dispatch(onSetQtyCount(0));
    };

    const percentagePrinted = () => {
        if (dispAct == null) return 0;
        if (dispAct.scheduledPallets === 0) return 100;
        return ((dispAct.producedPallets ?? 0) / dispAct.scheduledPallets) * 100;
    };

    if (dispAct == null || lblRdx.activities.length !== 1 || lblRdx.activities[0] == null) {
        return <Spinner visible={true} marginsClass={"mt-5"} />;
    } else if (
        params.lineId &&
        parseInt(params.lineId) &&
        !aplSettings.hasPermByLineId(UserPermission.OnlineLabellingFull, parseInt(params.lineId))
    ) {
        return (
            <LabelsPrintBasic
                isPrinting={lblRdx.printingProgress > 0}
                lineDescription={dispAct.lineDescription}
                workorder={dispAct.workOrder}
                materialDescription={dispAct.description1}
                nextSequence={dispAct.nextSequence}
                onPrint={executePrinting}
                aplSettings={aplSettings}
                translateString={translateString}
            />
        );
    } else {
        return (
            <React.Fragment>
                <div id="tblPalStatError" className="alert alert-danger hide" role="alert">
                    {/* <!-- error message placeholder --> */}
                </div>

                <div className="container mw-100">
                    {/* <!-- header text --> */}
                    <div className="row">
                        <div className="col">
                            <h2
                                className="rounded-lg pl-2"
                                style={{
                                    backgroundColor: aplSettings.theme.labellingColour ? aplSettings.theme.labellingColour : "inherit",
                                    color: aplSettings.theme.labellingColour ? getTextColor(aplSettings.theme.labellingColour) : "inherit"
                                }}
                            >
                                {t("main.printLabels")}
                            </h2>
                        </div>
                    </div>

                    <div className="container-grey-rounded">
                        <div>&nbsp;</div>
                        <form>
                            <div className="form-row">
                                <div className="col-sm-10 mb-1">
                                    <h5>{t("main.mainDetails")}</h5>
                                </div>

                                <div className="col-sm-2 mb-1">
                                    <div className="custom-control custom-switch float-right bs-switch" id="offline-switch">
                                        <input
                                            type="checkbox"
                                            className="custom-control-input"
                                            id="swOffline"
                                            checked={lblRdx.offline}
                                            disabled={!aplSettings.hasPermByLineId(UserPermission.OfflineLabelling, Number(params.lineId))}
                                            onChange={e => {
                                                handleChangeOfflineMode(e.target.checked);
                                            }}
                                        />
                                        <label className="custom-control-label" htmlFor="swOffline">
                                            {t("main.offlinePrint")}
                                        </label>
                                        <br />
                                        <Link
                                            to={""}
                                            className="hide float-right"
                                            id="lnkOfflineGpo"
                                            onClick={() => {
                                                alert("selectOfflineWorkorder()");
                                            }}
                                        >
                                            {t("main.workOrder")}
                                        </Link>
                                    </div>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="col-sm-6 mb-1">
                                    <label htmlFor="validationServer01">{t("main.palletsProduced")}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="txtPalletProduced"
                                        value={(dispAct.producedPallets ?? 0).toString() + " / " + dispAct.scheduledPallets.toString()}
                                        readOnly={true}
                                        onChange={_.noop}
                                    />
                                    <div className="valid-feedback">{/* <%-- Looks good! --%> */}</div>
                                </div>
                                <div className="col-sm-6 mb-1">
                                    <label>{t("main.qtyProduced")}</label>
                                    <div className="progress" style={{ height: "2rem" }}>
                                        <div
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{
                                                width: `${percentagePrinted() > 3 ? percentagePrinted() : 3}%`
                                            }}
                                            aria-valuenow={25}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            id="progressbar"
                                        >
                                            {`${percentagePrinted().toFixed(0)}%`}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>&nbsp;</div>

                            <div className="form-row">
                                <div className="col-sm-3 mb-1">
                                    <label htmlFor="txtWorkOrder">{t("main.workOrder")}</label>
                                    <div className="input-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control font-weight-bold"
                                            id="txtWorkOrder"
                                            aria-describedby="WorkOrder"
                                            readOnly={true}
                                            value={dispAct.workOrder}
                                            onChange={_.noop}
                                        />
                                        {lblRdx.offline && (
                                            <div className="input-group-append">
                                                <button
                                                    id="btn-save-number-of-copies"
                                                    className="btn btn-sm btn-primary"
                                                    type="button"
                                                    onClick={() => {
                                                        dispatch(clearWorkorderManagement());
                                                        navigate("/workordermanagement");
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="16"
                                                        height="16"
                                                        fill="currentColor"
                                                        className="bi bi-search"
                                                        viewBox="0 0 16 16"
                                                    >
                                                        <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* show batch for nordic sites */}
                                {[8, 22].includes(aplSettings.siteId) && (
                                    <div className="col-sm-3 mb-1">
                                        <label htmlFor="txtBatch">{t("main.batch")}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="txtBatch"
                                            aria-describedby="Batch"
                                            readOnly={true}
                                            value={dispAct.batch ?? ""}
                                            onChange={_.noop}
                                        />
                                    </div>
                                )}
                                <div className="col-sm-3 mb-1">
                                    <label htmlFor="txtWorkCenter">{t("main.workCenter")}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="txtWorkCenter"
                                        aria-describedby="WorkCenter"
                                        readOnly={true}
                                        value={`${dispAct.lineSapCode}`}
                                        onChange={_.noop}
                                    />
                                </div>
                                <div className="col-sm-3 mb-1">
                                    <label htmlFor="txtLine">{t("main.line")}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="txtLine"
                                        aria-describedby="Line"
                                        readOnly={true}
                                        value={dispAct.lineDescription}
                                        onChange={_.noop}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="col-sm-12 mb-1">
                                    <label htmlFor="txtPartDescription">{t("main.partDescription")}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="txtPartDescription"
                                        aria-describedby="PartDescription"
                                        readOnly={true}
                                        value={dispAct.materialSapCode + " - " + dispAct.description1}
                                        onChange={_.noop}
                                    />
                                </div>
                            </div>
                            <div className="form-row">
                                {[7, 22].includes(aplSettings.siteId) ? (
                                    <>
                                        <div className="col-sm-12 mb-1">
                                            <label htmlFor="textbox-CrCusMaterialCode">{t("main.litCustMat")}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="crCusMaterialCode"
                                                aria-describedby="CrCusMaterialCode"
                                                readOnly={true}
                                                value={dispAct.crCusMaterialCode ?? ""}
                                                onChange={_.noop}
                                            />
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="col-sm-12 mb-1">
                                            <label htmlFor="textbox-custCrossRef">{t("main.custCrossRef")}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="custCrossRef"
                                                aria-describedby="CustomerDescription"
                                                readOnly={true}
                                                value={getCustDescriptionField(dispAct)}
                                                onChange={_.noop}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="form-row">
                                <div className="col-sm-3 mb-1">
                                    <label htmlFor="txtRank">{t("main.nextSeqNo")}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="txtRank"
                                        aria-describedby="Rank"
                                        required
                                        readOnly
                                        value={dispAct.nextSequence}
                                        onChange={_.noop}
                                    />
                                </div>
                                <div className="col-sm-2 mb-1" id="divQuantityDrop">
                                    <label htmlFor="ddQuantity">{t("main.layers")}</label>
                                    <ReactSelectWrapper
                                        data={layersSelectData}
                                        isClearable={false}
                                        styles={ReactSelectStyle}
                                        onChange={handleDdQuantityChange as any}
                                        value={layersSelectData.find((x: any) => x.value === dispQty) || null}
                                        isDisabled={
                                            !aplSettings.hasPermByLineId(UserPermission.LabellingQuantityLayersEditable, dispAct.lineId)
                                        }
                                    />
                                </div>
                                <div className="col-sm-2 mb-1" id="divQuantityTxt">
                                    <label htmlFor="txtQuantity">{t("main.quantity")}</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        id="txtQuantity"
                                        aria-describedby="Text-Quantity"
                                        required
                                        disabled={
                                            lblRdx.printingProgress > 0 ||
                                            !aplSettings.hasPermByLineId(UserPermission.LabellingQuantityEditable, dispAct.lineId)
                                        }
                                        value={dispQty}
                                        onChange={e => {
                                            dispatch(onSetQtyCount(e.currentTarget.valueAsNumber) as any);
                                        }}
                                    />
                                </div>
                                <div className="col-sm-2 mb-1">
                                    <label htmlFor="txtNumberOfCopies" className="nowrap">
                                        {t("main.numberOfCopies")}
                                    </label>
                                    <div className="input-group mb-3">
                                        <input
                                            type="number"
                                            className="form-control"
                                            id="txtNumberOfCopies"
                                            aria-describedby="NumberOfCopies"
                                            required
                                            value={dispCopies}
                                            onChange={e => {
                                                dispatch(onSetCopiesCount(e.currentTarget.valueAsNumber) as any);
                                                dispatch(onSetCopesPerPalletChanged(true));
                                            }}
                                            disabled={lblRdx.printingProgress > 0}
                                        />
                                        <div className="input-group-append">
                                            <button
                                                id="btn-save-number-of-copies"
                                                className="btn btn-sm btn-primary"
                                                disabled={!lblRdx.userNumberOfCopiesPerPalletChanged}
                                                type="button"
                                                onClick={() => {
                                                    dispatch(
                                                        setUserNumberOfCopiesPerPalletRedux({
                                                            operationId: lblRdx.activities[0].operationId,
                                                            value: lblRdx.selectedCopiesCount
                                                        }) as any
                                                    );
                                                }}
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="16"
                                                    height="16"
                                                    fill="currentColor"
                                                    className="bi bi-check-circle"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"></path>
                                                    <path d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-3 mb-1">
                                    <label htmlFor="ddPrinterName">{t("main.printerName")}</label>
                                    <ReactSelectWrapper
                                        data={printersSelectData}
                                        isClearable={false}
                                        styles={ReactSelectStyle}
                                        onChange={handleChangePrinters as any}
                                        value={printersSelectData.find((x: any) => x.value === dispPrnt) || null}
                                        isDisabled={lblRdx.printingProgress > 0}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="col-sm-3 mb-1">
                                    <label htmlFor="txtLabelName">{t("main.labelName")}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="txtLabelName"
                                        aria-describedby="LabelName"
                                        required
                                        readOnly
                                        defaultValue={dispAct.palLabelName}
                                    />
                                </div>
                                {parseBool(
                                    aplSettings.siteParams.find(x => x.aplParameterType === AplParameter.LabelTraceability)?.value
                                ) && (
                                    <React.Fragment>
                                        <div className="col-sm-2 mb-1" id="txt-pallet-traceability-batch">
                                            <label htmlFor="txtDate">{t("main.traceabilityInfo")}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="txt-pallet-traceability-batch"
                                                aria-describedby="TraceabilityBatch"
                                                value={dispAct.palletTraceabilityBatch ?? ""}
                                                placeholder={dispAct.palletTraceabilityBatch ?? ""}
                                                readOnly={true}
                                            />
                                        </div>
                                    </React.Fragment>
                                )}

                                {lblRdx.offline ? (
                                    <React.Fragment>
                                        <div className="col-sm-2 mb-1" id="offlineDate">
                                            <label htmlFor="txtDate">{t("main.date")}</label>
                                            <div>
                                                <DateSelector3
                                                    name="labels-print-txt-date"
                                                    culture={aplSettings.culture}
                                                    onSetSelectedDate={e => {
                                                        dispatch(onSetOfflineTimeStamp(e));
                                                    }}
                                                    selectedDate={moment(lblRdx.offlineTimeStamp).toDate()}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-sm-2 mb-1 " id="offlineNoOfPallets">
                                            <label htmlFor="txt-pallet-count" className="nowrap">
                                                {t("main.noOfPallets")}
                                            </label>
                                            <input
                                                type="number"
                                                className="form-control nowr"
                                                id="txt-pallet-count"
                                                aria-describedby="pallet-count"
                                                value={lblRdx.selectedPalletCount}
                                                onChange={e => {
                                                    dispatch(onSetPalletCount(e.currentTarget.valueAsNumber));
                                                }}
                                                disabled={lblRdx.printingProgress > 0}
                                            />
                                        </div>
                                        <div className="col-sm-2 mb-1" id="offlineTime">
                                            {/* <label htmlFor="txtTime">{t("main.time")}</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="txtTime"
                                                aria-describedby="pallet-datetime"
                                                required
                                                disabled={true}
                                            /> */}
                                        </div>
                                    </React.Fragment>
                                ) : null}

                                <div className="col-sm-3 mb-1">{/* <!-- empty space --> */}</div>
                            </div>

                            <div className="float-right">
                                {lblRdx.printingProgress === 0 ? (
                                    <React.Fragment>
                                        {lblRdx.combinedPdf ? <DownloadButton /> : null}
                                        &nbsp;
                                        <button
                                            id="btnPrint"
                                            className="btn btn-primary print-page-button nowrap"
                                            type="button"
                                            onClick={() => {
                                                executePrinting();
                                            }}
                                        >
                                            {lblRdx.printingProgress > 0 ? (
                                                <span
                                                    id="btnRefreshSpinner"
                                                    className="spinner-border spinner-border-sm"
                                                    role="status"
                                                    aria-hidden={true}
                                                ></span>
                                            ) : (
                                                <div id="btnRefreshSpinner"></div>
                                            )}
                                            {`${t("main.print")} (F8)`}
                                        </button>
                                    </React.Fragment>
                                ) : null}
                                {lblRdx.printingProgress > 0 ? (
                                    <div className="btn-group">
                                        <button id="btn-cancel" className="btn btn-success print-page-button nowrap" type="button">
                                            {`${lblRdx.printingProgress} / ${
                                                lblRdx.selectedPalletCount === 0 ? 1 : lblRdx.selectedPalletCount
                                            }`}
                                            &nbsp; &nbsp;
                                            <span
                                                id="btnRefreshSpinner"
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-hidden={true}
                                            ></span>
                                        </button>
                                        <button id="btn-cancel" className="btn btn-danger" type="button" onClick={cancelPrinting}>
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="16"
                                                height="16"
                                                fill="currentColor"
                                                className="bi bi-x-circle"
                                                viewBox="0 0 16 16"
                                            >
                                                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                                            </svg>
                                        </button>
                                    </div>
                                ) : null}
                                &nbsp;
                                <button
                                    id="btnBack"
                                    className="btn btn-primary print-page-button nowrap"
                                    type="button"
                                    onClick={() => {
                                        navigate("/labels");
                                    }}
                                >
                                    {t("main.back")}
                                </button>
                            </div>
                        </form>

                        <div>&nbsp;</div>
                        <div>&nbsp;</div>
                        <div>&nbsp;</div>
                    </div>
                </div>
            </React.Fragment>
        );
    }
};

export default LabelsPrint;
